import { codeFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Snackbar,
  Divider,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  GetApp as GetAppIcon,
  LocalShipping as LocalShippingIcon,
  Undo as UndoIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import { usePartCodeOnly } from '../../utils/partSearchPrefs';
import { can } from '../../utils/access';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import DataTable from '../shared/DataTable';
import StockReceiveDialog from './StockReceiveDialog';
import StockAdjustDialog from './StockAdjustDialog';
import StockCountDialog from './StockCountDialog';
import InventoryItemDialog from './InventoryItemDialog';
import InventoryValueReportDialog from './InventoryValueReportDialog';
import { downloadInventoryReport } from './inventoryReports';
import SmartReceiveDialog from './SmartReceiveDialog';
import AddPartDialog from '../maintenances/AddPartDialog';
import PartEditDialog from '../parts/PartEditDialog';
import PrintPartLabelsDialog from '../parts/PrintPartLabelsDialog';
import { formatQuantity } from '../../utils';
import UnitSuffix from '../shared/UnitSuffix';

const STORAGE_KEY = 'inventory_list_preferences';

const defaultPreferences = {
  sortField: 'part__name',
  sortDirection: 'asc',
  filters: {
    stockStatus: 'all', // all, lowStock, negativeStock
  },
  searchQuery: '',
};

function InventoryListView({ t }) {
  const { currentOrganization } = useUserStore();
  // Starting tracking (the add dialog's pick and its manual form) needs
  // inventory.manage, the same permission as receive / adjust / count, so the
  // button is not offered to a user who could only reach a 403.
  const canManageInventory = can(currentOrganization, 'inventory.manage');

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Reports menu state
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState(null);
  const [datedValueDialogOpen, setDatedValueDialogOpen] = useState(false);
  const [reportError, setReportError] = useState(null);

  // Dialog states
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogInitialTab, setItemDialogInitialTab] = useState(0);
  const [smartReceiveDialogOpen, setSmartReceiveDialogOpen] = useState(false);
  const [smartReturnDialogOpen, setSmartReturnDialogOpen] = useState(false);
  const [editPartDialogOpen, setEditPartDialogOpen] = useState(false);
  const [editPart, setEditPart] = useState(null);
  const [addPartDialogOpen, setAddPartDialogOpen] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(null);

  // Multiselect state
  const [selectedIds, setSelectedIds] = useState(new Set());
  // null, or 'selection' while the label dialog is open for the ticked rows (read at
  // render time: DataTable keeps selectionActions from its first render).
  const [labelPartIds, setLabelPartIds] = useState(null);
  const [bulkDisableDialogOpen, setBulkDisableDialogOpen] = useState(false);
  const [bulkDeleteParts, setBulkDeleteParts] = useState(false);
  const [bulkDisabling, setBulkDisabling] = useState(false);

  // Summary stats (fetched from dedicated endpoint)
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockCount: 0,
    negativeCount: 0,
    totalValue: '0.00',
  });

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    handleSort: handleLocalSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Code-only search is shared with the part pickers, so it lives in its own
  // org-scoped key rather than this view's preference blob.
  const [codeOnly, setCodeOnly] = usePartCodeOnly();

  // Build server-side filter params from local preferences
  const serverFilters = useMemo(() => {
    const filters = {};
    if (preferences.filters.stockStatus === 'lowStock') {
      filters.low_stock = true;
    } else if (preferences.filters.stockStatus === 'negativeStock') {
      filters.negative = true;
    }
    if (codeOnly) {
      filters.code_only = true;
    }
    return filters;
  }, [preferences.filters, codeOnly]);

  // Use paginated list hook for server-side data fetching
  const {
    data: items,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/inventory/', {
    initialFilters: serverFilters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local preferences to server filters
  useEffect(() => {
    setServerFilters(serverFilters);
  }, [serverFilters, setServerFilters]);

  // Sync sort from local preferences to server
  useEffect(() => {
    setSort(preferences.sortField, preferences.sortDirection);
  }, [preferences.sortField, preferences.sortDirection, setSort]);

  // Sync search query from preferences to hook
  useEffect(() => {
    setSearchQuery(preferences.searchQuery);
  }, [preferences.searchQuery, setSearchQuery]);

  // Handle sort
  const handleSort = useCallback((field) => {
    handleLocalSort(field);
  }, [handleLocalSort]);

  // Update filters in local preferences
  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (preferences.filters.stockStatus !== 'all') count++;
    return count;
  }, [preferences.filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];
    if (preferences.filters.stockStatus === 'lowStock') {
      chips.push({ key: 'lowStock', label: t('inventory.lowStock') });
    } else if (preferences.filters.stockStatus === 'negativeStock') {
      chips.push({ key: 'negativeStock', label: t('inventory.negativeStock') });
    }
    return chips;
  }, [preferences.filters, t]);

  // Fetch summary stats from dedicated endpoint
  const fetchSummary = useCallback(async () => {
    if (!currentOrganization?.organization_id) return;
    try {
      const response = await axios.get(
        `/api/inventory/summary/?organization=${currentOrganization.organization_id}`
      );
      setStats({
        totalItems: response.data.total_items,
        lowStockCount: response.data.low_stock_count,
        negativeCount: response.data.negative_count,
        totalValue: response.data.total_value,
      });
    } catch (err) {
      console.error('Failed to fetch inventory summary:', err);
    }
  }, [currentOrganization?.organization_id]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const handleContextMenu = (event, item) => {
    event.stopPropagation();
    setSelectedItem(item);
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleDownloadReport = async (reportType, format = 'pdf') => {
    setReportsMenuAnchor(null);
    try {
      await downloadInventoryReport(reportType, format);
    } catch (err) {
      console.error('Report download failed:', err);
      setReportError(t('inventory.reportDownloadFailed'));
    }
  };

  const handleDialogSuccess = () => {
    refetch();
    fetchSummary();
  };

  const handleOpenEditPart = async (inventoryItem) => {
    try {
      const response = await axios.get(
        `/api/parts/${inventoryItem.part_id}/?organization=${currentOrganization.organization_id}`
      );
      setEditPart(response.data);
      setEditPartDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch part for editing:', err);
    }
  };

  const handleBulkDisableTracking = async () => {
    setBulkDisabling(true);
    try {
      const response = await axios.post(
        `/api/inventory/bulk-disable-tracking/?organization=${currentOrganization.organization_id}`,
        {
          inventory_ids: Array.from(selectedIds),
          delete_parts: bulkDeleteParts,
        },
        { headers: { 'X-CSRFToken': getCsrfToken() } }
      );
      const { disabled_count, deleted_count } = response.data;
      let msg = t('inventory.bulkDisable.successDisabled', { count: disabled_count });
      if (deleted_count > 0) {
        msg += '. ' + t('inventory.bulkDisable.successDeleted', { count: deleted_count });
      }
      setSuccessSnackbar(msg);
      setSelectedIds(new Set());
      setBulkDisableDialogOpen(false);
      setBulkDeleteParts(false);
      refetch();
      fetchSummary();
    } catch (err) {
      console.error('Bulk disable tracking failed:', err);
    } finally {
      setBulkDisabling(false);
    }
  };

  // Error snackbar
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const prevError = useRef(error);

  useEffect(() => {
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  return (
    <Box data-testid="inventory-page">
      <InventoryValueReportDialog
        open={datedValueDialogOpen}
        onClose={() => setDatedValueDialogOpen(false)}
        onError={(message) => setReportError(message)}
      />

      <Snackbar
        open={Boolean(reportError)}
        autoHideDuration={6000}
        onClose={() => setReportError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setReportError(null)} severity="error" sx={{ width: '100%' }}>
          {reportError}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            data-testid="inventory-heading"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('inventory.title')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('inventory.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canManageInventory && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setAddPartDialogOpen(true)}
            size="small"
            data-testid="add-part-button"
            sx={{
              borderRadius: '10px',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('inventory.addPart')}
          </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<LocalShippingIcon />}
            onClick={() => setSmartReceiveDialogOpen(true)}
            size="small"
            data-testid="smart-receive-button"
            sx={{
              borderRadius: '10px',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('inventory.smartReceive.button')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UndoIcon />}
            onClick={() => setSmartReturnDialogOpen(true)}
            size="small"
            data-testid="smart-return-button"
            sx={{
              borderRadius: '10px',
              borderColor: 'divider',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('inventory.smartReturn.button')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
            size="small"
            data-testid="inventory-reports-button"
            sx={{
              borderRadius: '10px',
              borderColor: 'divider',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('inventory.reports')}
          </Button>
        </Box>
        <Menu
          anchorEl={reportsMenuAnchor}
          open={Boolean(reportsMenuAnchor)}
          onClose={() => setReportsMenuAnchor(null)}
        >
          <MenuItem
            data-testid="inventory-report-value-pdf"
            onClick={() => handleDownloadReport('value', 'pdf')}
          >
            {t('inventory.valueReport')} (PDF)
          </MenuItem>
          <MenuItem
            data-testid="inventory-report-value-xlsx"
            onClick={() => handleDownloadReport('value', 'xlsx')}
          >
            {t('inventory.valueReport')} (Excel)
          </MenuItem>
          <MenuItem
            data-testid="inventory-report-value-dated"
            onClick={() => {
              setReportsMenuAnchor(null);
              setDatedValueDialogOpen(true);
            }}
          >
            {t('inventory.valueReportDated.menuItem')}
          </MenuItem>
          <Divider />
          <MenuItem
            data-testid="inventory-report-count-sheet"
            onClick={() => handleDownloadReport('count-sheet', 'pdf')}
          >
            {t('inventory.countSheet')} (PDF)
          </MenuItem>
        </Menu>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('inventory.itemsTracked')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {stats.totalItems}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('inventory.totalValue')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {formatCurrency(stats.totalValue)}
            </Typography>
          </CardContent>
        </Card>

        {stats.lowStockCount > 0 && (
          <Card sx={{ minWidth: 150, flex: 1 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="warning">
                {t('inventory.itemsLowStock')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                {stats.lowStockCount}
              </Typography>
            </CardContent>
          </Card>
        )}

        {stats.negativeCount > 0 && (
          <Card sx={{ minWidth: 150, flex: 1 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="error">
                {t('inventory.itemsNegative')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.dark' }}>
                {stats.negativeCount}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={codeOnly ? t('partSearch.codeOnlyPlaceholder') : t('inventory.searchPlaceholder')}
          data-testid="inventory-search"
        />
        <Tooltip title={t('partSearch.codeOnlyHint')}>
          <span>
            <FilterChip
              icon={<TagIcon />}
              label={t('partSearch.codeOnly')}
              active={codeOnly}
              onClick={() => setCodeOnly(!codeOnly)}
              color="primary"
              data-testid="parts-search-code-only"
            />
          </span>
        </Tooltip>
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('inventory.stockStatusFilter')}>
          <FilterChip
            icon={<WarningIcon />}
            label={t('inventory.filters.lowStock')}
            active={preferences.filters.stockStatus === 'lowStock'}
            onClick={() => updateFilters({ stockStatus: preferences.filters.stockStatus === 'lowStock' ? 'all' : 'lowStock' })}
            color="warning"
          />
          <FilterChip
            icon={<ErrorIcon />}
            label={t('inventory.filters.negativeStock')}
            active={preferences.filters.stockStatus === 'negativeStock'}
            onClick={() => updateFilters({ stockStatus: preferences.filters.stockStatus === 'negativeStock' ? 'all' : 'negativeStock' })}
            color="error"
          />
        </FilterSection>
      </FilterPanel>

      {/* Desktop Table - Hidden on mobile */}
      <DataTable
        columns={[
          {
            key: 'part_manufacturer_code',
            label: t('Code'),
            sortable: true,
            sortField: 'part__manufacturer_code',
            maxWidth: 150,
            render: (item) => {
              const searchLower = (preferences.searchQuery || '').toLowerCase().replace(/[\s\-\./\\]/g, '');
              const displayCode = item.part_primary_code || item.part_manufacturer_code;
              const matchedIdentifier = searchLower && item.part_identifiers
                ? item.part_identifiers.find((id) =>
                    id.code_normalized.toLowerCase().includes(searchLower) &&
                    id.code !== displayCode
                  )
                : null;
              return (
                <Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      ...codeFont,
                      color: 'primary.main',
                      wordBreak: 'break-word',
                    }}
                  >
                    {displayCode || '-'}
                  </Typography>
                  {matchedIdentifier && (
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontStyle: 'italic' }}>
                      {t('identifier_found_by_code', { code: matchedIdentifier.code })}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
          {
            key: 'part_name',
            label: t('Part'),
            sortable: true,
            sortField: 'part__name',
            maxWidth: 300,
            render: (item) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                {item.part_name}
              </Typography>
            ),
          },
          {
            key: 'shelf_location',
            label: t('inventory.shelfLocation'),
            sortable: true,
            hideBelow: 'xl',
            render: (item) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {item.shelf_location || '-'}
              </Typography>
            ),
          },
          {
            key: 'quantity_in_stock',
            label: t('inventory.quantityInStock'),
            sortable: true,
            align: 'right',
            noWrap: true,
            render: (item) => (
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: item.is_negative ? 'error.main' : 'text.primary',
                }}
              >
                {formatQuantity(item.quantity_in_stock)}
                <UnitSuffix unit={item.part_unit} />
              </Typography>
            ),
          },
          {
            key: 'quantity_reserved',
            label: t('inventory.quantityReserved'),
            sortable: true,
            sortField: 'annotated_reserved',
            align: 'right',
            noWrap: true,
            hideBelow: 'lg',
            render: (item) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {formatQuantity(item.quantity_reserved)}
              </Typography>
            ),
          },
          {
            key: 'quantity_free',
            label: t('inventory.quantityFree'),
            sortable: true,
            sortField: 'annotated_free',
            align: 'right',
            noWrap: true,
            render: (item) => (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: parseFloat(item.quantity_free) < 0 ? 'error.main' : 'text.primary',
                }}
              >
                {formatQuantity(item.quantity_free)}
              </Typography>
            ),
          },
          {
            key: 'quantity_on_order',
            label: t('inventory.onOrder'),
            align: 'right',
            noWrap: true,
            hideBelow: 'xl',
            render: (item) => (
              <Typography sx={{ fontSize: '13px', color: parseFloat(item.quantity_on_order) > 0 ? 'info.main' : 'text.secondary' }}>
                {parseFloat(item.quantity_on_order) > 0 ? formatQuantity(item.quantity_on_order) : '-'}
              </Typography>
            ),
          },
          {
            key: 'stock_value',
            label: t('inventory.stockValue'),
            sortable: true,
            sortField: 'avg_unit_cost',
            align: 'right',
            noWrap: true,
            hideBelow: 'lg',
            render: (item) => (
              <Typography sx={{ fontSize: '13px' }}>
                {item.stock_value ? formatCurrency(item.stock_value) : '-'}
              </Typography>
            ),
          },
          {
            key: 'status',
            label: t('Status'),
            sortable: true,
            sortField: 'status_sort',
            align: 'center',
            noWrap: true,
            render: (item) => (
              item.is_negative ? (
                <Chip size="small" label={t('inventory.negativeStock')} color="error" icon={<ErrorIcon />} sx={{ fontSize: '11px' }} />
              ) : item.is_low_stock ? (
                <Chip size="small" label={t('inventory.lowStock')} color="warning" icon={<WarningIcon />} sx={{ fontSize: '11px' }} />
              ) : null
            ),
          },
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (item) => (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleContextMenu(e, item);
                }}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        data={items}
        loading={loading}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        rowIdField="id"
        selectionActions={[
          {
            label: t('partLabels.printLabels'),
            icon: <PrintIcon />,
            onClick: () => setLabelPartIds('selection'),
          },
          {
            label: t('inventory.bulkDisable.action'),
            icon: <RemoveCircleOutlineIcon />,
            onClick: () => setBulkDisableDialogOpen(true),
            color: 'error',
          },
        ]}
        onRowClick={(item) => {
          setSelectedItem(item);
          setItemDialogInitialTab(0);
          setItemDialogOpen(true);
        }}
        sortField={preferences.sortField}
        sortDirection={preferences.sortDirection}
        onSort={handleSort}
        testIdPrefix="inventory-row"
        tableTestId="inventory-table"
        emptyIcon={<InventoryIcon />}
        emptyTitle={preferences.searchQuery || activeFilterCount > 0 ? t('inventory.noItemsMatch') : t('inventory.noItems')}
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }} data-testid="inventory-table-mobile">
        {/* Loading overlay for refetch */}
        {loading && items.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && items.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {preferences.searchQuery || activeFilterCount > 0
                ? t('inventory.noItemsMatch')
                : t('inventory.noItems')}
            </Typography>
          </Paper>
        ) : (
          items.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              data-testid={`inventory-row-mobile-${item.id}`}
              onClick={() => {
                setSelectedItem(item);
                setItemDialogInitialTab(0);
                setItemDialogOpen(true);
              }}
              sx={{
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                  {/* Inventory Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: item.is_negative
                        ? 'rgba(211, 47, 47, 0.1)'
                        : item.is_low_stock
                        ? 'rgba(237, 108, 2, 0.1)'
                        : 'rgba(49, 130, 206, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.is_negative
                        ? 'error.main'
                        : item.is_low_stock
                        ? 'warning.main'
                        : 'secondary.main',
                      flexShrink: 0,
                    }}
                  >
                    <InventoryIcon />
                  </Box>

                  {/* Item Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.25,
                      }}
                    >
                      {item.part_name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      {(item.is_negative || item.is_low_stock) && (
                        <Chip
                          size="small"
                          label={item.is_negative ? t('inventory.negativeStock') : t('inventory.lowStock')}
                          color={item.is_negative ? 'error' : 'warning'}
                          icon={item.is_negative ? <ErrorIcon /> : <WarningIcon />}
                          sx={{ height: 20, fontSize: '10px' }}
                        />
                      )}
                      {(item.part_primary_code || item.part_manufacturer_code) && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            ...codeFont,
                            color: 'primary.main',
                          }}
                        >
                          {item.part_primary_code || item.part_manufacturer_code}
                        </Typography>
                      )}
                      {item.shelf_location && (
                        <Chip
                          size="small"
                          label={item.shelf_location}
                          variant="outlined"
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                      )}
                    </Box>

                    {/* Stock details row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {t('inventory.quantityInStock')}:
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 600,
                            ml: 0.5,
                            color: item.is_negative ? 'error.main' : 'text.primary',
                          }}
                        >
                          {formatQuantity(item.quantity_in_stock)}
                          <UnitSuffix unit={item.part_unit} />
                        </Typography>
                      </Typography>
                      {parseFloat(item.quantity_reserved) > 0 && (
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                          {t('inventory.reserved')}:
                          <Typography component="span" sx={{ fontWeight: 500, ml: 0.5 }}>
                            {formatQuantity(item.quantity_reserved)}
                          </Typography>
                        </Typography>
                      )}
                      {parseFloat(item.quantity_on_order) > 0 && (
                        <Typography sx={{ fontSize: '13px', color: 'info.main' }}>
                          {t('inventory.onOrder')}:
                          <Typography component="span" sx={{ fontWeight: 500, ml: 0.5 }}>
                            {formatQuantity(item.quantity_on_order)}
                          </Typography>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Value and Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}>
                    {item.stock_value ? formatCurrency(item.stock_value) : '-'}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, item);
                    }}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Mobile Pagination */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          data-testid="inventory-action-receive"
          onClick={() => {
            handleCloseContextMenu();
            setReceiveDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('inventory.receiveStock')}</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="inventory-action-adjust"
          onClick={() => {
            handleCloseContextMenu();
            setAdjustDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <RemoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('inventory.adjustStock')}</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="inventory-action-count"
          onClick={() => {
            handleCloseContextMenu();
            setCountDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <InventoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('inventory.physicalCount')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          data-testid="inventory-action-history"
          onClick={() => {
            handleCloseContextMenu();
            setItemDialogInitialTab(2);
            setItemDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('inventory.viewHistory')}</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="inventory-action-edit-part"
          onClick={() => {
            handleCloseContextMenu();
            handleOpenEditPart(selectedItem);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Edit Part')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <PrintPartLabelsDialog
        open={labelPartIds !== null}
        onClose={() => setLabelPartIds(null)}
        // Labels are per part; the rows are inventory items of those parts.
        partIds={labelPartIds ? (items || []).filter((item) => selectedIds.has(item.id)).map((item) => item.part_id) : []}
      />
      {selectedItem && (
        <>
          <StockReceiveDialog
            open={receiveDialogOpen}
            onClose={() => setReceiveDialogOpen(false)}
            inventoryItem={selectedItem}
            onSuccess={handleDialogSuccess}
          />
          <StockAdjustDialog
            open={adjustDialogOpen}
            onClose={() => setAdjustDialogOpen(false)}
            inventoryItem={selectedItem}
            onSuccess={handleDialogSuccess}
          />
          <StockCountDialog
            open={countDialogOpen}
            onClose={() => setCountDialogOpen(false)}
            inventoryItem={selectedItem}
            onSuccess={handleDialogSuccess}
          />
          <InventoryItemDialog
            open={itemDialogOpen}
            onClose={() => setItemDialogOpen(false)}
            inventoryItem={selectedItem}
            onSuccess={handleDialogSuccess}
            initialTab={itemDialogInitialTab}
            onEditPart={() => {
              setItemDialogOpen(false);
              handleOpenEditPart(selectedItem);
            }}
          />
        </>
      )}

      {/* Part Edit Dialog */}
      <PartEditDialog
        open={editPartDialogOpen}
        onClose={() => {
          setEditPartDialogOpen(false);
          setEditPart(null);
        }}
        part={editPart}
        onPartUpdated={handleDialogSuccess}
      />

      {/* Add Part Dialog */}
      <AddPartDialog
        open={addPartDialogOpen}
        handleClose={() => setAddPartDialogOpen(false)}
        mode="inventory"
        onPartCreated={handleDialogSuccess}
      />

      {/* Smart Receive Dialog */}
      <SmartReceiveDialog
        open={smartReceiveDialogOpen}
        onClose={() => setSmartReceiveDialogOpen(false)}
        onSuccess={(result) => {
          handleDialogSuccess();
          if (result) {
            const parts = [];
            parts.push(t('inventory.smartReceive.success.received', { count: result.received_count }));
            if (result.new_parts_count > 0) {
              parts.push(t('inventory.smartReceive.success.newParts', { count: result.new_parts_count }));
            }
            if (result.tracking_enabled_count > 0) {
              parts.push(t('inventory.smartReceive.success.trackingEnabled', { count: result.tracking_enabled_count }));
            }
            setSuccessSnackbar(parts.join('. '));
          }
        }}
      />

      {/* Smart Return Dialog */}
      <SmartReceiveDialog
        mode="return"
        open={smartReturnDialogOpen}
        onClose={() => setSmartReturnDialogOpen(false)}
        onSuccess={(result) => {
          handleDialogSuccess();
          if (result) {
            setSuccessSnackbar(t('inventory.smartReturn.success.returned', { count: result.returned_count }));
          }
        }}
      />

      {/* Bulk Disable Tracking Confirm Dialog */}
      <Dialog
        open={bulkDisableDialogOpen}
        onClose={() => {
          if (!bulkDisabling) {
            setBulkDisableDialogOpen(false);
            setBulkDeleteParts(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('inventory.bulkDisable.title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('inventory.bulkDisable.confirm', { count: selectedIds.size })}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('inventory.bulkDisable.historyPreserved')}
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkDeleteParts}
                onChange={(e) => setBulkDeleteParts(e.target.checked)}
                disabled={bulkDisabling}
                color="error"
              />
            }
            label={t('inventory.bulkDisable.alsoDeleteParts')}
          />
          {bulkDeleteParts && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('inventory.bulkDisable.deleteWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkDisableDialogOpen(false);
              setBulkDeleteParts(false);
            }}
            disabled={bulkDisabling}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDisableTracking}
            disabled={bulkDisabling}
          >
            {bulkDisabling ? <CircularProgress size={20} /> : t('inventory.bulkDisable.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={6000}
        onClose={() => setSuccessSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar(null)} severity="success" sx={{ width: '100%' }}>
          {successSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(InventoryListView);
